import { Redirect } from "react-router-dom";
import { ProviderSet } from "@app/provider-sets";
import CreateProject from "@app/projects/create/createProject";
import WizardStep from "@app/projects/create/WizardStep";
import Features from "@app/feature-flags/features";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ProviderSetService from "@app/provider-sets/providerSetService";
import { ProviderSetAliasKeys } from "@app/projects/create/interfaces";

interface CreateProjectStepProvidersProps {
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  hideUseInMarketAnalysis: boolean;
  isZipProject: boolean;
  onProvidersChange: (ps: ProviderSet) => void;
  onProspectsChange: (ps: ProviderSet) => void;
  onUseMarketChange: (state: boolean) => void;
  onUseMarketAnalysisChange: (state: boolean) => void;
  onUseProspectAnalysisChange: (state: boolean) => void;
  onUseNetworkAnalysisChange: (state: boolean) => void;
  onMarketChange: (ps: ProviderSet) => void;
  onProviderSetAliasChange: (key: ProviderSetAliasKeys, value: string) => void;
  networkAlias: string;
  prospectAlias: string;
  marketAlias: string;
  ncpAlias: string;

  progressList: string[];
  currentIndex: number;
}

const CreateProjectStepProviders: FC<CreateProjectStepProvidersProps> = (props: CreateProjectStepProvidersProps) => {
  const providerSetService = useRef(new ProviderSetService()).current;
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const [loading, setLoading] = useState(true);
  const [marketProviderSets, setMarketProviderSets] = useState<ProviderSet[]>([]);
  const [providerSets, setProviderSets] = useState<ProviderSet[]>([]);
  const [market, setMarket] = useState<ProviderSet | null>(null);
  const [prospects, setProspects] = useState<ProviderSet | null>(null);
  const [providers, setProviders] = useState<ProviderSet | null>(null);
  const [useMarketProvidersForAnalysis, setUseMarketProvidersForAnalysis] = useState(props.createProjectModel.useMarketProvidersForMarketAnalysis);
  const [useNetworkProvidersForAnalysis, setUseNetworkProvidersForAnalysis] = useState(props.createProjectModel.useNetworkProvidersForMarketAnalysis);
  const [useProspectProvidersForAnalysis, setUseProspectProvidersForAnalysis] = useState(props.createProjectModel.useProspectProvidersForMarketAnalysis);
  const [restartApp] = useState(!props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step));
  const isNotZipOrAllowsZipImpact = !props.isZipProject || enabledFeatureFlags?.has(Features.ZIP_PROSPECT_AND_MARKET);

  useEffect(() => {
    const promises = [providerSetService.getMarketProviderSets(), providerSetService.getProviderSets()];
    Promise.all(promises).then(([newMarketProviderSets, newProviderSets]) => {
      setLoading(false);
      setMarketProviderSets(newMarketProviderSets);
      setProviderSets(newProviderSets);
    })
  }, []);

  /** Renders a select list, alias field, and checkbox for a particular type of provider set. */
  const renderNetworkType = (
    label: "Network" | "Prospect" | "Market",
    providerSets: Map<string, ProviderSet[]>,
    selectedProviderSet: ProviderSet | null,
    handler: React.ChangeEventHandler<HTMLSelectElement>,
    checkboxHandler: React.ChangeEventHandler<HTMLInputElement>,
    selectedCheckBoxValue: boolean,
    selectedValue: number,
    hideUseInMarketAnalysis: boolean,
    alias: string,
    aliasHandler: (key: ProviderSetAliasKeys, alias: string) => void,
    autofocus?: boolean,
  ) => {
    let aliasKey: ProviderSetAliasKeys;
    switch (label) {
      case "Network":
        aliasKey = "networkProviderSetAlias";
        break;
      case "Prospect":
        aliasKey = "prospectProviderSetAlias";
        break;
      case "Market":
        aliasKey = "marketProviderSetAlias";
        break;
    }

    const commonSelectProps: React.SelectHTMLAttributes<HTMLSelectElement> = {
      onChange: handler,
      value: selectedProviderSet ? selectedProviderSet.resource.resourceId : "",
    };

    const optGroups: JSX.Element[] = [];
    providerSets.forEach((providerSet, setLabel) => {
      if (setLabel === "") {
        providerSet.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
          // for some reason, the statuses return as "active" even though their type is an enum
          optGroups.push(providerSet.hasActivatedVersions ?
            <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
            <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>)))
      } else {
        optGroups.push(<optgroup label={setLabel} key={setLabel}> {
          providerSet.sort((a, b) => a.hasActivatedVersions > b.hasActivatedVersions ? -1 : 1).map((providerSet) => (
            providerSet.hasActivatedVersions ?
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId}>{providerSet.resource.name}</option> :
              <option key={providerSet.resource.resourceId} value={providerSet.resource.resourceId} disabled={true}>{providerSet.resource.name} - No Activated Versions</option>))
        }
        </optgroup>)
      }
    });

    const select = (
      <QESSelect
        label="Provider Set"
        {...commonSelectProps}
        value={selectedValue || ""}
        testId={"create-project-step-providers__" + label + "-select"}
        disabled={loading}
        autoFocus={autofocus}
        allowEmpty={true}
        hideLabel={true}
      >
        {
          optGroups
        }
      </QESSelect>
    );

    return (
      <>
        <div className="form-group row">
          <div className="col-1 align-items-center d-flex label-s">{label}</div>
          <div className="col-11 d-flex">
            <div className="col-4 pe-3">{select}</div>
            {isNotZipOrAllowsZipImpact && (
              <QESTextInput
                containerClassName="col-3 pe-3"
                label={"Alias"}
                value={alias || ""}
                onChange={(e) => { aliasHandler(aliasKey, e.target.value) }}
                testId={`create-project-step-providers__${label}-alias`}
                disabled={loading || !selectedValue}
                hideLabel={true}
                maxLength={255}
              />
            )}
          </div>
          {!hideUseInMarketAnalysis &&
            <div className="col-4 offset-1 ps-4">
              <QESCheckbox
                label={"Use " + label + " Providers for Market Analysis"}
                checked={selectedCheckBoxValue}
                disabled={!selectedValue}
                onChange={checkboxHandler}
                testId={"create-project-step-providers__use-" + label + "-providers-for-analysis-checkbox"}
              />
            </div>
          }
        </div>
      </>
    );
  }

  const handleIsNextDisabled = () => {
    return !(
      props.createProjectModel.networkProviderSetId !== null &&
      props.createProjectModel.networkProviderSetId !== 0
    );
  }

  /** Handles when the provider set for providers is changed. */
  const handleProvidersChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      const id = Number(value);
      const providerSet = providerSets.find((ps) => ps.resource.resourceId === id);
      setProviders(providerSet);
      props.onProvidersChange(providerSet);
    } else {
      setProviders(null);
      setUseNetworkProvidersForAnalysis(false);
      props.onProvidersChange(null);
      props.onUseNetworkAnalysisChange(false);
      props.onProviderSetAliasChange("networkProviderSetAlias", "");
    }
  }

  /** Handles when the provider set for prospects is changed. */
  const handleProspectsChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value) {
      const id = Number(value);
      const providerSet = providerSets.find((ps) => ps.resource.resourceId === id);
      setProspects(providerSet);
      props.onProspectsChange(providerSet);
    } else {
      setProspects(null);
      setUseProspectProvidersForAnalysis(false);
      props.onProspectsChange(null);
      props.onUseProspectAnalysisChange(false);
      props.onProviderSetAliasChange("prospectProviderSetAlias", "");
    }
  }

  /** Handles when the "Use Market Providers" checkbox is checked or unchecked. */
  const handleUseMarketProvidersChange = (checked: boolean) => {
    setUseMarketProvidersForAnalysis(checked);
    props.onUseMarketChange(checked);
  }

  const handleUseMarketProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseMarketProvidersForAnalysis(e.target.checked);
    props.onUseMarketAnalysisChange(e.target.checked);
  }

  const handleUseNetworkProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseNetworkProvidersForAnalysis(e.target.checked);
    props.onUseNetworkAnalysisChange(e.target.checked);
  }

  const handleUseProspectProvidersForAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUseProspectProvidersForAnalysis(e.target.checked);
    props.onUseProspectAnalysisChange(e.target.checked);
  }

  /** Handles when the provider set for prospects is changed. */
  const handleMarketChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value) {
      const id = Number(value);
      let providerSet = marketProviderSets.find((ps) => ps.resource.resourceId === id);

      if (!providerSet)
        providerSet = providerSets.find((ps) => ps.resource.resourceId === id);

      setMarket(providerSet);
      props.onMarketChange(providerSet);
      handleUseMarketProvidersChange(true);
    } else {
      setMarket(null);
      props.onMarketChange(null);
      handleUseMarketProvidersChange(false);
      props.onProviderSetAliasChange("marketProviderSetAlias", "");
    }
  }

  /** Renders the wizard step that selects providers and prospects. */

  if (restartApp) {
    return <Redirect to={`${props.baseUrl}/1`} />;
  }

  return (
    <WizardStep {...props} heading="Create a New Project" subheading="Define Analysis :: Providers" baseUrl={props.baseUrl} prevUrl={(props.currentIndex - 1).toString()} nextUrl={(props.currentIndex + 1).toString()} progressList={props.progressList} isNextDisabled={handleIsNextDisabled()}>
      <div className="">
        <div className="row">
          <div className="col-11 d-flex flex-wrap offset-1">
            <div className="col-4 label-s">Provider Set</div>
            {isNotZipOrAllowsZipImpact && <div className="col-3 label-s">Alias</div>}
          </div>
        </div>
        {renderNetworkType("Network", new Map([["", providerSets]]), providers, handleProvidersChanged, handleUseNetworkProvidersForAnalysisChange, useNetworkProvidersForAnalysis, props.createProjectModel.networkProviderSetId, props.hideUseInMarketAnalysis, props.networkAlias, props.onProviderSetAliasChange, true)}
        {isNotZipOrAllowsZipImpact && (
          <>
            {renderNetworkType("Prospect", new Map([["", providerSets]]), prospects, handleProspectsChanged, handleUseProspectProvidersForAnalysisChange, useProspectProvidersForAnalysis, props.createProjectModel.prospectProviderSetId, props.hideUseInMarketAnalysis, props.prospectAlias, props.onProviderSetAliasChange)}
            {renderNetworkType("Market", new Map([["System Networks", marketProviderSets], ["Client Networks", providerSets]]), market, handleMarketChanged, handleUseMarketProvidersForAnalysisChange, useMarketProvidersForAnalysis, props.createProjectModel.marketProviderSetId, true, props.marketAlias, props.onProviderSetAliasChange)}
          </>
        )}
        <div className="form-group row">
          <div className="col-1 align-items-center d-flex label-s text-nowrap">Next Closest {props.isZipProject ? "Providers" : "Network Providers"}</div>
          <div className="col-11 d-flex flex-wrap">
              <QESTextInput
                containerClassName="col-3 offset-4"
                label={"Alias"}
                value={props.ncpAlias || ""}
                onChange={(e) => { props.onProviderSetAliasChange("nextClosestProviderSetAlias", e.target.value) }}
                testId={`create-project-step-providers__ncp-alias`}
                disabled={loading}
                hideLabel={true}
                maxLength={255}
              />
          </div>
        </div>
      </div>
    </WizardStep >
  );
}
export default CreateProjectStepProviders;